import { Injectable } from '@angular/core';

import { EventMediaHelperService } from '@frontend/sports/betting-offer/feature/media';
import { EventDetailsColumnType, EventModel } from '@frontend/sports/betting-offer/feature/model';
import { MediaConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { getAnimationIconForSport, getVideoIcon } from '@frontend/sports/common/core/feature/icon';
import { EventSwitcherItem, MediaModuleContextPage } from '@frontend/sports/grid/media/feature/model';
import { MediaState } from '@frontend/sports/grid/media/feature/state';
import { TrackingVideoSource } from '@frontend/sports/tracking/feature';
import { findIndex } from 'lodash-es';

import { EventDetailsHelperService } from '../event-details-common/event-details-helper.service';

export interface MediaComponentState {
    isAnimationAvailable: boolean;
    isStatsExpanderVisible: boolean;
    isEventLive: boolean;
    isEventDetailPage: boolean;
    isExpanded: boolean;
    isLocationAllowsExpand: boolean;
    restoreExpandState: boolean;
    isDefaultExpanded: boolean;
    collapsed: boolean;
    tabs: MediaTabBarItem[];
    videoEventId: string | null;
    videoEvent: EventModel | null;
    animationEvent: EventModel | null;
    statisticsEvent: EventModel | null;
    activeTab: EventDetailsColumnType;
    videoDropdownEvents: EventSwitcherItem[];
    videoPinned: boolean;
    videoUnpinInfoVisible: boolean;
    videoAutoplay: boolean;
    videoPlaying: boolean;
    pinEnabled: boolean;
    hasEventOnMedia: boolean;
    trackingSource: TrackingVideoSource;
    pendingEsport?: EventModel;
}

export interface MediaTabBarItem {
    id: number;
    title?: string;
    icon?: string;
    disabled?: boolean;
    active?: boolean;
}

@Injectable({ providedIn: 'root' })
export class MediaComponentMapService {
    private readonly defaultBackgroundColor = 'black';

    constructor(
        private eventDetailsHelper: EventDetailsHelperService,
        private eventMediaHelperService: EventMediaHelperService,
        private mediaConfig: MediaConfig,
    ) {}

    mapContent(mediaState: MediaState): MediaComponentState {
        const animationEvent = mediaState.animation.eventId ? mediaState.eventInfos[mediaState.animation.eventId].event : null;
        const statisticsEvent = mediaState.statistics.eventId ? mediaState.eventInfos[mediaState.statistics.eventId].event : null;
        const videoEvent = mediaState.video.eventId ? mediaState.eventInfos[mediaState.video.eventId].event : null;
        const isAnimationAvailable = !!animationEvent && this.eventMediaHelperService.hasAnimation(animationEvent);
        const isStatsExpanderVisible = !(statisticsEvent && statisticsEvent.sport.id === SportConstant.Soccer);
        const isEventLive = videoEvent ? videoEvent.live : false;
        let hasSecondEventStream = false;
        if (mediaState.context.eventId && Object.keys(mediaState.eventInfos).length > 1) {
            const secondEvent = mediaState.eventInfos[mediaState.context.eventId];
            if (secondEvent) {
                hasSecondEventStream = secondEvent.event.stream !== undefined;
            }
        }

        const isEventDetailPage = mediaState.context.page === MediaModuleContextPage.EventDetails;
        const collapsed = mediaState.isCollapsed;

        return {
            trackingSource: mediaState.video.trackingSource,
            isAnimationAvailable,
            isStatsExpanderVisible,
            isEventLive,
            isEventDetailPage,
            isExpanded: mediaState.mediaExpandState.isExpanded,
            isLocationAllowsExpand: mediaState.mediaExpandState.isLocationAllowsExpand,
            restoreExpandState: mediaState.mediaExpandState.restoreExpandState,
            isDefaultExpanded: mediaState.mediaExpandState.isDefaultExpanded,
            collapsed,
            videoEventId: mediaState.video.eventId,
            videoEvent,
            animationEvent,
            statisticsEvent,
            activeTab:
                !this.mediaConfig.showVideoStreaming && mediaState.activeTab === EventDetailsColumnType.Video
                    ? EventDetailsColumnType.Animation
                    : mediaState.activeTab,
            videoDropdownEvents: mediaState.video.dropdownEvents,
            videoPinned: mediaState.video.pinned,
            videoUnpinInfoVisible: mediaState.video.unpinInfoVisible && hasSecondEventStream,
            videoAutoplay: mediaState.video.autoplay,
            videoPlaying: mediaState.video.playing && mediaState.activeTab === EventDetailsColumnType.Video && videoEvent !== null,
            tabs: this.mapTabs(mediaState),
            pinEnabled: !!mediaState.video.eventId && mediaState.video.pinEnabled,
            hasEventOnMedia: Object.keys(mediaState.eventInfos).length > 0,
            pendingEsport: mediaState.pendingEsport,
        };
    }

    mapTabs(mediaState: MediaState): MediaTabBarItem[] {
        const tabs: MediaTabBarItem[] = [];
        const sportId = mediaState.animation.eventId ? mediaState.eventInfos[mediaState.animation.eventId].event.sport.id : SportConstant.Soccer;
        const eventId = mediaState.statistics.eventId ? mediaState.eventInfos[mediaState.statistics.eventId].event : null;

        if (this.mediaConfig.showVideoStreaming) {
            tabs.push({
                id: EventDetailsColumnType.Video,
                title: mediaState.video.pinned ? '<span class="theme-pin-pinned"></span>' : '',
                icon: getVideoIcon(false),
            });
        }
        tabs.push({
            id: EventDetailsColumnType.Animation,
            title: '',
            icon: getAnimationIconForSport(sportId, false),
            disabled: mediaState.animation.eventId
                ? !this.eventMediaHelperService.hasAnimationOrSimulation(mediaState.eventInfos[mediaState.animation.eventId].event)
                : true,
        });
        if (this.mediaConfig.showStatsCenterButton) {
            tabs.push({
                id: EventDetailsColumnType.Stats,
                title: '',
                icon: 'sports-stats',
                disabled:
                    !eventId ||
                    this.eventMediaHelperService.hasPerformPlayerEnabled(eventId) ||
                    !(this.eventDetailsHelper.hasStats(eventId) || eventId.statsId),
            });
        }

        let activeIndex = findIndex(tabs, (tab) => tab.id === mediaState.activeTab);

        if (activeIndex < 0) {
            activeIndex = 0;
        }
        tabs[activeIndex].active = true;

        return tabs;
    }

    getBackgroundcolor(mediaState: MediaComponentState, type: EventDetailsColumnType): string {
        if (type === EventDetailsColumnType.Video) {
            return this.getBackground(mediaState.videoEvent, true);
        } else if (type === EventDetailsColumnType.Animation) {
            return this.getBackground(mediaState.animationEvent, mediaState.isAnimationAvailable);
        } else if (type === EventDetailsColumnType.Stats) {
            return this.getBackground(mediaState.statisticsEvent, false);
        } else {
            return this.getBackground(mediaState.animationEvent, false);
        }
    }

    private getBackground(animationEvent: EventModel | null, isAnimationAvailable: boolean): string {
        if (animationEvent === null) {
            return this.defaultBackgroundColor;
        }

        return this.eventDetailsHelper.getBackgroundColor(animationEvent, isAnimationAvailable) || this.defaultBackgroundColor;
    }
}
